import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User } from "lucide-react";
import logo from "../images/logo3.png";
import AnchorLink from "./AnchorLink";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedName = localStorage.getItem("username");
    setIsAuthenticated(!!token);
    setUsername(savedName || "");
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    setIsAuthenticated(false);
    setUsername("");
    navigate("/login");
  };

  const menuItems = [
    { label: "О нас", to: "/about", type: "link" },
    { label: "Новости", to: "#news", type: "anchor" },
    { label: "Филиалы", to: "#map", type: "anchor" },
    { label: "Команда", to: "/team", type: "link" },
    { label: "Партнёры", to: "/partners", type: "link" },
    { label: "Реквизиты", to: "/donate", type: "link" },
    { label: "Контакты", to: "#contacts", type: "anchor" },
  ];

  const NavLink = ({ item, onClick, className = "" }) => {
    const baseClass = `relative group ${className}`;

    const content = (
      <>
        {item.label}
        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-500 to-teal-500 group-hover:w-full transition-all duration-300" />
      </>
    );

    if (item.type === "link") {
      return (
        <Link to={item.to} onClick={onClick} className={baseClass}>
          {content}
        </Link>
      );
    }
    return (
      <AnchorLink to={item.to} onClick={onClick} className={baseClass}>
        {content}
      </AnchorLink>
    );
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/90 backdrop-blur-xl shadow-glass py-3"
            : "bg-gradient-to-b from-dark-900/70 via-dark-900/30 to-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link to="/" className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-teal-500 rounded-xl blur-lg opacity-30" />
                <img src={logo} alt="Logo" className="relative h-10 w-auto" />
              </div>
              <span
                className={`text-lg font-bold transition-colors duration-300 ${
                  isScrolled ? "text-dark-900" : "text-white"
                }`}
              >
                Жасыл Желкен
              </span>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {menuItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <NavLink
                  item={item}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-300 ${
                    isScrolled
                      ? "text-dark-700 hover:text-primary-600 hover:bg-primary-50"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  }`}
                />
              </motion.div>
            ))}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to="/cabinet"
                    title="Личный кабинет"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isScrolled
                        ? "bg-primary-50 text-primary-700 hover:bg-primary-100"
                        : "bg-white/10 text-white hover:bg-white/20"
                    }`}
                  >
                    <User className="w-4 h-4" />
                    {username}
                  </Link>
                </motion.div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleLogout}
                  className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-teal-500 text-white shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300"
                >
                  Выйти
                </motion.button>
              </>
            ) : (
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/login"
                  className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-teal-500 text-white shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300"
                >
                  Войти
                </Link>
              </motion.div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden relative w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
              isScrolled || isOpen
                ? "text-dark-900 hover:bg-dark-100"
                : "text-white hover:bg-white/10"
            }`}
            aria-label="Toggle menu"
          >
            <div className="w-5 h-4 flex flex-col justify-between">
              <motion.span
                animate={{
                  rotate: isOpen ? 45 : 0,
                  y: isOpen ? 7 : 0,
                }}
                className={`block h-0.5 w-full rounded-full transition-colors ${
                  isScrolled || isOpen ? "bg-dark-900" : "bg-white"
                }`}
              />
              <motion.span
                animate={{
                  opacity: isOpen ? 0 : 1,
                  scaleX: isOpen ? 0 : 1,
                }}
                className={`block h-0.5 w-full rounded-full transition-colors ${
                  isScrolled || isOpen ? "bg-dark-900" : "bg-white"
                }`}
              />
              <motion.span
                animate={{
                  rotate: isOpen ? -45 : 0,
                  y: isOpen ? -7 : 0,
                }}
                className={`block h-0.5 w-full rounded-full transition-colors ${
                  isScrolled || isOpen ? "bg-dark-900" : "bg-white"
                }`}
              />
            </div>
          </motion.button>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-dark-900/60 backdrop-blur-sm z-40 lg:hidden"
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-full max-w-sm bg-white z-50 lg:hidden overflow-y-auto"
            >
              <div className="p-6">
                {/* Close button */}
                <div className="flex justify-end mb-8">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsOpen(false)}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-dark-100 text-dark-600"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                </div>

                {/* Navigation */}
                <nav className="space-y-1">
                  {menuItems.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <NavLink
                        item={item}
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-3 text-lg font-medium text-dark-700 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      />
                    </motion.div>
                  ))}
                </nav>

                {/* Auth Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-8 pt-8 border-t border-dark-100"
                >
                  {isAuthenticated ? (
                    <div className="space-y-4">
                      <Link
                        to="/cabinet"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 bg-primary-50 hover:bg-primary-100 rounded-xl transition-colors"
                      >
                        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-primary-700" />
                        </div>
                        <div>
                          <p className="text-xs text-dark-500">Личный кабинет</p>
                          <p className="font-semibold text-primary-700">{username}</p>
                        </div>
                      </Link>
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          handleLogout();
                        }}
                        className="w-full px-4 py-3 text-center text-red-600 font-medium rounded-xl border-2 border-red-200 hover:bg-red-50 transition-colors"
                      >
                        Выйти
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="block w-full px-4 py-3 text-center text-white font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-teal-500 shadow-lg"
                    >
                      Войти
                    </Link>
                  )}
                </motion.div>

                {/* Footer info */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-auto pt-8"
                >
                  <p className="text-sm text-dark-400 text-center">
                    Жасыл Желкен &copy; 2024
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
